'use client'

import { LegacyPaymentToken, PaymentToken, fetchPaymentToken, updateCartPaymentMethod } from '@/api/commerce-api'
import { postPaymentLegacy } from '@/api/legacy/commerce-legacy-api'
import useSession from '@/app/(public)/auth/_components/hooks/use-session'
import WaitDialog from '@/app/components/@modals/wait-dialog'
import ExpirationDateField from '@/app/components/forms/fields/expiration-date-field'
import RadioInputField, { RadioInputOption } from '@/app/components/forms/fields/radio-input-field'
import StyledRadioInputField from '@/app/components/forms/fields/styled-radio-input-field'
import TextInputField from '@/app/components/forms/fields/text-input-field'
import crypto from '@/lib/crypto'
import { logger } from '@/lib/logger'
import { cFormatter } from '@/lib/utils'
import { load as cheerio } from 'cheerio'
import { Field, Form, Formik, useFormikContext } from 'formik'
import ky from 'ky'
import { get, sumBy } from 'lodash'
import { AlertCircle, CreditCard, Landmark, Lock } from 'lucide-react'
import { useRouter } from 'next/navigation'
import { serialize } from 'object-to-formdata'
import qs from 'qs'
import { useEffect } from 'react'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { twMerge } from 'tailwind-merge'
import { useCartStoreActions } from '../../../../stores/cart-store'
import { Cart, ProPaySeamlessResponseCipher, UpdateCartPaymentMethodRequest } from '../../_lib/commerce'
import { cartTotals } from '../../_lib/signals/checkout'
import { PaymentFormSchema, paymentFormValidator } from '../../_lib/validation/PaymentFormValidation'
import CreditCardNumberInputField from './credit-card-number-input-field'

type CheckoutFormProps = {
  closeModal?: () => void
  cart: Cart
}

const tokenizedPaymentMethod = async (request: any, token: string): Promise<ProPaySeamlessResponseCipher> => {
  const { url, paymentMethod, ...payload } = request
  const params = new URLSearchParams({
    ...payload,
  })
  const req = await ky.post(url, { body: params })
  const html = await req.text()
  logger.debug('Response HTML:', html)
  const $ = cheerio(html) as any
  const result = $('#ResponseCipher').val() as string
  if (result == null) {
    const qry = qs.parse(html)
    return qry as unknown as ProPaySeamlessResponseCipher
  }

  const data = crypto.decrypt(result, token!)
  if (data) {
    return qs.parse(data) as unknown as ProPaySeamlessResponseCipher
  }
  return {} as ProPaySeamlessResponseCipher
}

type CheckoutCartEffectsProps = {
  cart: Cart
}

// Syncs cart totals into the Formik form. Rendered as a real component inside
// Formik so its hooks run in a valid React function component.
const CheckoutCartEffects = ({ cart }: CheckoutCartEffectsProps) => {
  const { setFieldValue, values } = useFormikContext<PaymentFormSchema>()
  useEffect(() => {
    setFieldValue('subtotal', cart!.subtotal)
    setFieldValue('merchantId', cart!.merchant?.merchantProfileId)
    setFieldValue('fees', cart!.fees)
    setFieldValue('total', cart!.total)
    if (Number(cart!.total) === 0) setFieldValue('paymentType', 'None')
    else setFieldValue('paymentType', cart!.rate?.paymentMethodType ?? 'CreditCard')
  }, [cart])
  useEffect(() => {
    const totals = {
      fees: cart!.fees || 0,
      total: cart?.total || 0,
      subtotal: cart?.subtotal || 0,
      totalItems: cart?.totalItems || 0,
      merchantPays: cart?.merchant?.merchantPaysFees || false,
    }
    if (values.paymentType == 'CreditCard') {
      totals.fees =
        sumBy(
          cart.items.filter((i) => i.amount > 0),
          (i) => i.qty * (cart.merchant?.creditCardItemFee || 0),
        ) +
        sumBy(cart.items, (i) => i.itemAmount) * ((cart.merchant?.creditCardTransactionPercentageFee || 0) / 100) +
        (sumBy(cart.items, (i) => i.itemAmount) > 0 ? cart.merchant?.creditCardTransactionFee || 0 : 0)
      totals.total = (cart.merchant?.merchantPaysFees ? 0 : totals.fees) + cart.subtotal + cart.tax
      setFieldValue('fees', totals.fees)
      setFieldValue('total', totals.total)
      if (Number(totals.total) === 0) setFieldValue('paymentType', 'None')
    }
    if (values.paymentType == 'ACH') {
      totals.fees =
        sumBy(
          cart.items.filter((i) => i.amount > 0),
          (i) => i.qty * (cart.merchant?.achItemFee || 0),
        ) +
        (sumBy(cart.items, (i) => i.itemAmount) * (cart.merchant?.achTransactionPercentageFee || 0)) / 100 +
        (sumBy(cart.items, (i) => i.itemAmount) > 0 ? cart.merchant?.achTransactionFee || 0 : 0)
      totals.total = (cart.merchant?.merchantPaysAchFees ? 0 : totals.fees) + cart.subtotal + cart.tax

      setFieldValue('fees', totals.fees)
      setFieldValue('total', totals.total)
      if (Number(totals.total) === 0) setFieldValue('paymentType', 'None')
    }
    const { logo, ...merch } = cart.merchant ?? {}
    cartTotals.value = totals
  }, [values.paymentType])
  return null
}

const CheckoutForm = ({ closeModal, cart }: CheckoutFormProps) => {
  const router = useRouter()
  const { executeRecaptcha } = useGoogleReCaptcha()
  const { session } = useSession()

  const initValues = {
    email: session?.email ?? '',
    paymentType: Number(cart?.total ?? 0) === 0 ? 'None' : cart!.rate?.paymentMethodType ?? 'CreditCard',
    cardHolderName: '',
    cardNumber: '',
    cardType: '',
    expirationDate: '',
    cvv: '',
    zipCode: '',
    bankAccountType: 'Checking',
    nameOnBankAccount: '',
    bankAccountNumber: '',
    routingNumber: '',
    subtotal: cart?.subtotal ?? 0,
    fees: cart?.fees ?? 0,
    total: cart?.total ?? 0,
  } as PaymentFormSchema
  const paymentTypeSelectorOptions: RadioInputOption[] = [
    { label: 'Credit Card', value: 'CreditCard', icon: CreditCard },
    { label: 'E-Check', value: 'ACH', icon: Landmark, selectable: cart?.merchant?.allowAch ?? true },
    { label: 'Cash', value: 'Cash', selectable: false },
    { label: 'No Payment', value: 'None', selectable: false },
  ]

  const createCypher = (propayToken: PaymentToken, formValues: PaymentFormSchema) => {
    const payload = {
      AuthToken: propayToken.token,
      PayerID: propayToken.payerId,
      PaymentProcessType: formValues.paymentType as any,
      ProcessMethod: 'None',
      PaymentMethodStorageOption: 'OnSuccess',
      CurrencyCode: 'USD',
      Amount: formValues.total ?? 0.0,
      InvoiceNumber: `${cart?.id}`,
      ReturnURL: get(window, 'location.href'),
      ...(formValues.paymentType === 'ACH' && {
        StandardEntryClassCode: 'WEB',
      }),
      ...(formValues.paymentType === 'CreditCard' && {
        StandardEntryClassCode: 'WEB',
      }),
    }
    const payloadQS = qs.stringify(payload)
    logger.debug('Payload QS:', { payloadQS, propayToken })
    const cypher = crypto.encrypt(payloadQS, propayToken.token!)
    logger.debug('Cypher:', cypher)
    return cypher
  }

  const createSpiPaymentMethod = async (propayToken: PaymentToken, formValues: PaymentFormSchema) => {
    logger.debug('Creating SPI Payment Method with values:', formValues)
    const cypher = createCypher(propayToken, formValues)
    logger.debug('Cypher created:', cypher)
    const seamlessRequest = {
      url: propayToken.endpoint!,
      CID: propayToken.credentialId,
      SettingsCipher: cypher!,
      paymentMethod: formValues.paymentType,
      ...(formValues.paymentType === 'CreditCard'
        ? {
            CardHolderName: formValues.cardHolderName,
            ExpMonth: parseInt(formValues.expirationDate!.split('/')[0]),
            ExpYear: parseInt(formValues.expirationDate!.split('/')[1]),
            PaymentTypeId: formValues.cardType,
            CardNumber: formValues.cardNumber,
            Country: 'USA',
            PostalCode: formValues.zipCode,
            CVV: formValues.cvv,
          }
        : {
            BankAccountNumber: formValues.bankAccountNumber,
            BankAccountType: formValues.bankAccountType,
            BankCountryCode: 'USA',
            RoutingNumber: formValues.routingNumber,
            NameOnBankAccount: formValues.nameOnBankAccount,
          }),
    } as any
    logger.debug('Seamless Request:', seamlessRequest)
    const result = await tokenizedPaymentMethod(seamlessRequest, propayToken.token!)
    logger.debug('Seamless Response:', result)
    return result
  }
  const { reset } = useCartStoreActions()
  const redirectToConfirmation = () => {
    reset()
    // Only pass the cart key - the confirmation page will get all data from the cart API
    const url = `/pages/public/checkout-confirmation.html?cid=${cart!.key}`
    router.push(url)
  }
  const processPayment = async (values: PaymentFormSchema) => {
    const updatePaymentMethodPayload = {
      id: cart!.id,
      key: cart!.key,
      sessionId: cart!.sessionId,
      paymentType: values.paymentType,
      paymentSubType: (values.cardType ?? values.bankAccountType) as any,

      name: (values.cardHolderName ?? values.nameOnBankAccount)!,
      total: values.total!,
      fees: values.fees!,
      merchantId: cart!.merchant?.merchantProfileId!,
      postalCode: values.zipCode!,
      email: session?.email ?? values.email!,
    } as any as UpdateCartPaymentMethodRequest

    const payableTotal = Number(values.total ?? cart?.total ?? 0)
    if (payableTotal === 0) {
      const zeroResult = await postPaymentLegacy(values.email, cart!.key)
      if (zeroResult.Success === true) {
        redirectToConfirmation()
      } else {
        throw new Error(zeroResult?.Message ?? `Payment had an error`)
      }
      return
    }
    if (values.paymentType == 'CreditCard' || values.paymentType == 'ACH') {
      const propayToken = await fetchPaymentToken(values.total ?? 0)
      logger.info(`ProPay Token: ${JSON.stringify(propayToken)}`)

      const paymentInfo = await createSpiPaymentMethod(propayToken, values)

      logger.info(`paymentInfo: ${JSON.stringify(paymentInfo)}`)
      const tag = `submit_order${cart!.merchant?.tenantId ? '/' + cart!.merchant?.tenantId : ''}`
      logger.info(`Recaptcha tag: ${tag}`)
      const recaptchaToken = await executeRecaptcha!(tag)
      if (recaptchaToken == null) throw new Error('Recaptcha token is null')
      logger.debug(`recaptcha token: ${recaptchaToken}`)
      updatePaymentMethodPayload.obfuscatedAccountNumber = paymentInfo.ObfuscatedAccountNumber
      updatePaymentMethodPayload.payerId = paymentInfo.PayerID
      updatePaymentMethodPayload.paymentMethodId = paymentInfo.PaymentMethodId
      updatePaymentMethodPayload.city = paymentInfo.City!
      updatePaymentMethodPayload.state = paymentInfo.State!
      updatePaymentMethodPayload.expireDate = paymentInfo.ExpireDate
      updatePaymentMethodPayload.reCaptchaToken = recaptchaToken!

      if (paymentInfo.ExpireDate) updatePaymentMethodPayload.expireDate = paymentInfo.ExpireDate
      logger.info('updatePaymentMethodPayload', updatePaymentMethodPayload)
      const updated = await updateCartPaymentMethod(updatePaymentMethodPayload)
      if (updated === false) throw new Error('Failed to update payment method')
      var paymentResult = await postPaymentLegacy(values.email, cart!.key)
      if (paymentResult.Success === true) {
        redirectToConfirmation()
      } else {
        throw new Error(paymentResult?.Message ?? `Payment had an error`)
      }
    } else if (values.paymentType == 'None') {
      var paymentResult = await postPaymentLegacy(values.email, cart!.key)
      if (paymentResult.Success === true) {
        redirectToConfirmation()
      } else {
        throw new Error(paymentResult?.Message ?? `Payment had an error`)
      }
    }
    logger.debug('sent values: ', { values, key: cart.key })
  }
  return (
    <>
      <h2 id="payment-heading" className="sr-only">
        Payment details
      </h2>
      <Formik
        initialValues={initValues}
        validationSchema={paymentFormValidator}
        onSubmit={async (values, helpers) => {
          helpers.setFieldError('paymentType', '')
          helpers.setSubmitting(true)
          try {
            await processPayment(values)
          } catch (err) {
            helpers.setFieldError('paymentType', 'There was an error processing your payment')
          }

          // Reset the error for paymentType field

          helpers.setSubmitting(false)
        }}
      >
        {({ values, errors, isSubmitting, resetForm, handleChange }) => {
          return (
            <div className="mx-auto max-w-2xl px-4 lg:px-8">
              <CheckoutCartEffects cart={cart} />
              <Form>
                {errors?.paymentType && (
                  <div className="border-2 border-solid border-red-300 bg-red-100 px-2 py-2 text-sm text-red-500">
                    <AlertCircle className="mr-1 inline-block h-4 w-4" /> {errors.paymentType}
                  </div>
                )}
                {session && session.isAuthenticated ? (
                  <>
                    <Field type="hidden" name="email" />
                    <TextInputField
                      name="email"
                      label="Email"
                      type="email"
                      placeholder="Email"
                      className="col-span-8"
                      required={true}
                      value={session?.email ?? ''}
                      disabled={true}
                    />
                  </>
                ) : (
                  <>
                    <h3 id="contact-info-heading" className="my-4 text-lg font-medium text-gray-900">
                      Contact Information
                    </h3>
                    <div>
                      <TextInputField
                        name="email"
                        label="Email"
                        type="email"
                        placeholder="Email"
                        className="col-span-8"
                        required={true}
                      />
                    </div>
                  </>
                )}
                {values.total != 0 && (
                  <>
                    <h3 id="contact-info-heading" className="my-4 mt-8 text-lg font-medium text-gray-900">
                      Payment Information
                    </h3>
                    <StyledRadioInputField
                      name="paymentType"
                      options={paymentTypeSelectorOptions}
                      schema={paymentFormValidator}
                      className="col-span-8"
                      required={true}
                    />
                  </>
                )}

                <div className="mt-6 grid grid-cols-8 gap-x-4 gap-y-6">
                  {(values.paymentType as any) == 'CreditCard' && (
                    <>
                      <TextInputField
                        name="cardHolderName"
                        label="Card Holder Name"
                        className="col-span-8"
                        required={true}
                      />
                      <CreditCardNumberInputField
                        name="cardNumber"
                        label="Card Number"
                        className="col-span-8"
                        required={true}
                      />
                      <ExpirationDateField
                        name="expirationDate"
                        label="Exp Date"
                        className="col-span-3"
                        placeholder="MM/YY"
                        required={true}
                      />
                      <TextInputField name="cvv" label="CVV" className="col-span-2" required={true} />
                      <TextInputField name="zipCode" label="Zip Code" className="col-span-3" required={true} />
                    </>
                  )}
                  {(values.paymentType as any) == 'ACH' && (
                    <>
                      <RadioInputField
                        name="bankAccountType"
                        label="Account Type"
                        options={[
                          { label: 'Checking', value: 'Checking' },
                          { label: 'Savings', value: 'Savings' },
                        ]}
                        required={true}
                        className="col-span-8"
                      />
                      <TextInputField
                        name="nameOnBankAccount"
                        label="Account Holder Name"
                        className="col-span-8"
                        required={true}
                      />
                      <TextInputField
                        name="bankAccountNumber"
                        label="Account Number"
                        className="col-span-8"
                        required={true}
                      />
                      <TextInputField
                        name="routingNumber"
                        label="Routing Number"
                        className="col-span-8"
                        required={true}
                      />
                    </>
                  )}
                </div>

                <div className="col-span-8 mt-10 flex justify-end border-t border-gray-200 pt-6">
                  <div className="flex-1 text-xs uppercase text-gray-500">
                    <Lock className="mr-1 inline-block h-4 w-4 cursor-pointer text-gray-500 duration-300 ease-in-out hover:text-gray-900" />
                    Secured Payment
                  </div>
                  <button
                    type="button"
                    disabled={isSubmitting}
                    className={twMerge(
                      'rounded-md border border-transparent bg-slate-500 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 focus:ring-offset-gray-50',
                    )}
                    onClick={() => resetForm()}
                  >
                    Reset
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting || !executeRecaptcha}
                    className={twMerge(
                      'hover:bg-primary-700 focus:ring-primary-500 ml-2 rounded-md border border-transparent bg-primary px-4 py-2 text-sm font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-50',
                      isSubmitting ? 'cursor-not-allowed bg-slate-400' : 'bg-primary',
                    )}
                  >
                    Pay {cFormatter(cartTotals.value.total)}
                  </button>
                </div>
              </Form>

              {/* <div>
                  <pre>{JSON.stringify({ values, cart, cartSummary }, null, 2)}</pre>
                </div> */}

              <WaitDialog isOpen={isSubmitting} status={'Processing Payment'} />
            </div>
          )
        }}
      </Formik>
    </>
  )
}

export default CheckoutForm
